import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faHouse, faLock } from '@fortawesome/free-solid-svg-icons';
import { Button } from '@/components/ui/button';
import { ROUTES } from '@/routes/routes';

/**
 * Page-level permission denial, rendered INSIDE the authenticated app shell.
 *
 * ── WHY THIS IS NOT `AccessRestrictedPage` ──────────────────────────────────
 * There is an existing `AccessRestrictedPage`, and it is deliberately left
 * alone. That screen answers a different question: "this whole workspace may
 * not be used" — a suspended, expired or cancelled subscription, or an inactive
 * tenant. It is mounted OUTSIDE `AppLayout` and OUTSIDE `ProtectedRoute` so it
 * still renders once the session has been cleared, its only action is "Back to
 * sign in", and its reasons are kept in lockstep with the backend's
 * `SUBSCRIPTION_*` codes.
 *
 * This component answers "you are signed in and your workspace is fine, but
 * this one page is not yours". The remedy is different (ask your administrator,
 * not your service provider), the surroundings are different (the sidebar and
 * header stay, because everything else still works), and the actions are
 * different. Folding the two together would have meant adding a subscription
 * reason that is not a subscription reason, and breaking the test that pins
 * that reason set.
 *
 * Authorization is NOT decided here. `PermissionRoute` has already decided;
 * this only renders the answer. Rendering it grants nothing, and the backend
 * refuses the underlying API regardless of what this file does.
 *
 * ── WHAT IT DELIBERATELY DOES NOT SAY ───────────────────────────────────────
 * No "403", no permission string, no endpoint, no role name, no diagnostic. A
 * user cannot act on `role:view`, and naming it only tells someone probing the
 * app which permission to go after. The administrator can look it up.
 */

interface Props {
  /**
   * Optional override for the body copy, for a surface where the generic
   * wording would be less clear. The heading is fixed so the state is
   * recognisable wherever it appears.
   */
  description?: string;
}

export function AccessRestricted({ description }: Props) {
  const navigate = useNavigate();

  return (
    <div
      data-testid="access-restricted"
      className="flex flex-col items-center justify-center rounded-xl border border-border bg-card px-6 py-16 text-center"
    >
      {/* Lock mark. The ring uses a single CSS transition-free pulse that is
          switched off under `prefers-reduced-motion`, following the project's
          existing `motion-reduce:animate-none` convention (AuthorizationRadar,
          AttentionRequired). No library, no asset, no network request — the
          icon is the FontAwesome set the app already ships. */}
      <span className="relative mb-5 flex h-16 w-16 items-center justify-center">
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full bg-muted-foreground/10 animate-ping motion-reduce:animate-none"
        />
        <span className="relative flex h-16 w-16 items-center justify-center rounded-full bg-muted">
          <FontAwesomeIcon icon={faLock} className="h-6 w-6 text-muted-foreground" />
        </span>
      </span>

      <h2 className="text-lg font-semibold text-foreground">Access Restricted</h2>
      <p className="mt-1 text-sm font-medium text-foreground">
        You don&apos;t have permission to access this section.
      </p>
      <p className="mt-2 max-w-md text-sm text-muted-foreground">
        {description ??
          "Your current account doesn't have the required permissions to view this page. If you believe you should have access, contact your administrator."}
      </p>

      <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
        {/* Both are SPA navigations — `navigate`, never a location assignment —
            so the app is not reloaded and the router history stays intact. */}
        <Button onClick={() => navigate(ROUTES.ROOT)}>
          <FontAwesomeIcon icon={faHouse} className="mr-2 h-3.5 w-3.5" />
          Go to Dashboard
        </Button>
        <Button variant="secondary" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2 h-3.5 w-3.5" />
          Go Back
        </Button>
      </div>
    </div>
  );
}
